import React from "react";
import { Redirect } from "react-router-dom";
import { connect } from "react-redux";
import { User } from "oidc-client";
import UnauthorizedError from "../Errors/UnauthorizedError";
import { appPaths, IAppState, ForbiddenDisplay, roleService } from "../../Common";
import RedirectingToLoginProvider from "./RedirectingToLoginProvider";
import { Role } from "../../Common/Constants/roleEnums";

export enum ErrorHandling {
  ThrowError,
  RedirectToUnauthorized,
  ShowNothing,
  RedirectToAuth
}

type StateProps = { currentRoute: string; user?: User };
type Props = StateProps & { errorHandling?: ErrorHandling; roles?: Role[]; children: any };

// NOTE: Roles on this component are "at least one" - you do not need all roles specified
const ProtectedContent: React.FunctionComponent<Props> = props => {
  if (props.user) {
    // if user is defined, the user is authenticated
    // check roles if present
    let hasValidRole: boolean = !props.roles;
    if (!hasValidRole && !!props.roles) {
      // test each role
      let i;
      for (i = 0; i < props.roles.length; i++) {
        if (roleService.isUserInRole(props.user, props.roles[i])) {
          hasValidRole = true;
          break;
        }
      }
    }

    if (hasValidRole) {
      return <>{props.children}</>;
    }

    // special case- if we had roles and they were invalid, override RedirectToAuth
    if (!!props.roles && !hasValidRole && props.errorHandling === ErrorHandling.RedirectToAuth) {
      // default to show forbidden
      return (
        <ForbiddenDisplay description="You do not have the role required to access this page" />
      );
    }
  }

  // if we get to here, we have an error
  if (props.errorHandling === ErrorHandling.RedirectToAuth) {
    return <RedirectingToLoginProvider />;
  }
  if (props.errorHandling === ErrorHandling.ShowNothing) return null;
  if (props.errorHandling === ErrorHandling.RedirectToUnauthorized)
    return <Redirect to={appPaths.unauthorized} />;

  throw new UnauthorizedError({
    message: "User attempted to access protected content without privileges",
    path: props.currentRoute
  });
};

const mapStateToProps = (state: IAppState): StateProps => ({
  user: state.oidc.user,
  currentRoute: state.router.location.pathname + state.router.location.search
});

export default connect(mapStateToProps)(ProtectedContent);
